import { apiClient } from "./client";

// ============== Export Version ==============

export interface ExportVersionStats {
  variable_groups_count: number;
  variables_count: number;
  form_field_groups_count: number;
  form_fields_count: number;
  output_field_groups_count: number;
  output_fields_count: number;
  reference_tables_count: number;
  test_scenarios_count: number;
  proposal_templates_count: number;
}

export interface ExportedTestScenario {
  name: string;
  description: string | null;
  inputs: Record<string, unknown>;
  expected_outputs: Record<string, unknown>;
}

export interface ExportedProposalTemplate {
  name: string;
  description: string | null;
  is_active: boolean;
  pages_data: Array<{
    index: number;
    name: string;
    canvas_data: Record<string, unknown>;
  }>;
}

export interface ExportVersionResult {
  export_format_version: string;
  exported_at: string;
  exported_by: string;
  simulator: {
    name: string;
    slug: string;
    description: string | null;
  };
  version: {
    version_number: number;
    status: string | null;
    notes: string | null;
    appearance_options: Record<string, unknown> | null;
    settings: Record<string, unknown> | null;
  };
  data: {
    variable_groups: any[];
    form_field_groups: any[];
    output_field_groups: any[];
  };
  reference_tables: any[];
  test_scenarios: ExportedTestScenario[];
  proposal_templates: ExportedProposalTemplate[];
  stats: ExportVersionStats;
}

export async function exportVersion(
  versionId: number,
  additionalTableIds?: number[],
  _includeTestScenarios?: boolean // Not used - backend always includes test scenarios
): Promise<ExportVersionResult> {
  const params: { additional_table_ids?: string } = {};
  if (additionalTableIds && additionalTableIds.length > 0) {
    params.additional_table_ids = additionalTableIds.join(",");
  }
  const response = await apiClient.get<ExportVersionResult>(
    `/admin/export-version/${versionId}`,
    { params }
  );
  return response.data;
}

// ============== Sync from Distant Database ==============

export interface SyncCheckResult {
  available: boolean;
  is_local_environment: boolean;
  is_authorized: boolean;
  is_configured: boolean;
}

export async function checkSyncAvailability(): Promise<SyncCheckResult> {
  const response = await apiClient.get<SyncCheckResult>("/admin/sync-check");
  return response.data;
}

export interface SyncFromDistantStats {
  reference_tables_synced: number;
  simulators_synced: number;
  versions_synced: number;
  variable_groups_synced: number;
  variables_synced: number;
  form_field_groups_synced: number;
  form_fields_synced: number;
  output_field_groups_synced: number;
  output_fields_synced: number;
  access_rules_synced: number;
  test_scenarios_synced: number;
  proposal_templates_synced: number;
  simulations_synced: number;
  proposals_synced: number;
  proposal_versions_synced: number;
}

export interface SyncFromDistantResult {
  success: boolean;
  message: string;
  synced_at: string;
  synced_by: string;
  stats: SyncFromDistantStats;
}

export async function syncFromDistant(): Promise<SyncFromDistantResult> {
  const response = await apiClient.post<SyncFromDistantResult>("/admin/sync-from-distant");
  return response.data;
}
